import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const SUPPORT_EMAIL = 'support@socialworktestprepacademy.com';

/**
 * The public account-deletion page — the URL declared in the Google Play Data
 * safety form (`/delete-account`), which Play requires to exist, to be readable
 * WITHOUT signing in, and to state: who the app and developer are, the exact
 * steps to request deletion, what is deleted versus kept, and for how long.
 *
 * Deliberately static instructions, NOT a self-serve deletion form. An
 * unauthenticated endpoint that deletes an account from an emailed link is an
 * account-takeover vector, and this site already has the cautionary example:
 * `/unsubscribe?user_id=` acts on an id from the query string and writes a
 * field no sender reads. Deletion runs in the app (authenticated) or through a
 * human who verifies the request came from the account's own address.
 *
 * Every fact below is read off `aswb-backend/src/deleteAccount.ts` — the
 * collections it deletes, the Stripe cancel-or-abort ordering, and the customer
 * record it deliberately RETAINS for billing history. If that function changes,
 * this page is wrong until it is updated with it.
 */
const DeleteAccount: React.FC = () => {
  const canonical = 'https://www.socialworktestprepacademy.com/delete-account';

  return (
    <>
      <Helmet>
        <title>Delete Your Account | Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="How to delete your Social Work Test Prep Academy account, what data is deleted, what we keep, and for how long."
        />
        <meta property="og:title" content="Delete Your Account | Social Work Test Prep Academy" />
        <meta
          property="og:description"
          content="Steps to delete your Social Work Test Prep Academy account and exactly what happens to your data."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <div className="new-home-container">
        <div className="new-home-content">
          <div className="new-home-inner">
            <div className="delete-account">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center text-neutral-900">
                Delete your Social Work Test Prep Academy account
              </h1>
              <p className="text-base sm:text-lg mb-6 text-center text-neutral-600">
                Last updated: August 8, 2026
              </p>

              <p className="mb-6 text-neutral-700">
                <strong>App:</strong> Social Work Test Prep Academy (Android, iPhone and web).{' '}
                <strong>Developer:</strong> Social Work Test Prep Academy. This page explains how to
                delete your account and exactly what happens to your data. You do not need to sign in
                to read it.
              </p>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">
                1. Delete it yourself, in the app
              </h2>
              <p className="mb-2 text-neutral-700">
                This is the fastest way. Deletion starts immediately and cannot be undone.
              </p>
              <p className="mb-2 text-neutral-700"><strong>Android</strong></p>
              <ol className="list-decimal pl-5 mb-4 text-neutral-700">
                <li>Open the app and go to <strong>Profile</strong>.</li>
                <li>Tap <strong>Settings</strong>.</li>
                <li>Scroll to <strong>Account</strong> and tap <strong>Delete account</strong>.</li>
                <li>
                  Read what will be removed, tick <strong>I understand this can&apos;t be undone</strong>,
                  then tap <strong>Delete my account</strong>.
                </li>
              </ol>
              <p className="mb-2 text-neutral-700"><strong>iPhone</strong></p>
              <ol className="list-decimal pl-5 mb-4 text-neutral-700">
                <li>Open the app and go to <strong>Profile</strong>.</li>
                <li>Tap <strong>Delete Account</strong>.</li>
                <li>Confirm with <strong>Delete My Account</strong>.</li>
              </ol>
              <p className="mb-4 text-neutral-700">
                You will be signed out as soon as it finishes. There is nothing left to sign back
                into.
              </p>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">
                2. Or ask us to delete it for you
              </h2>
              <p className="mb-2 text-neutral-700">
                If you use the website, cannot open the app, or would simply rather we did it:
              </p>
              <ol className="list-decimal pl-5 mb-4 text-neutral-700">
                <li>
                  Email <a className="underline" href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20account`}>{SUPPORT_EMAIL}</a>{' '}
                  <strong>from the email address on your account</strong>.
                </li>
                <li>Put <strong>Delete my account</strong> in the subject line.</li>
                <li>
                  We reply to confirm, then delete the account. We aim to finish within 2 business
                  days, and always within 30 days of the request.
                </li>
              </ol>
              <p className="mb-4 text-neutral-700">
                We can only accept a deletion request sent from the account&apos;s own email address.
                If we accepted requests from anyone, anyone could delete your studying.
              </p>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">3. What is deleted</h2>
              <p className="mb-2 text-neutral-700">
                All of the following are permanently removed. None of it can be restored afterwards,
                by you or by us:
              </p>
              <ul className="list-disc pl-5 mb-4 text-neutral-700">
                <li>Your login (email address, password, and any Google or Apple sign-in link)</li>
                <li>Your profile, exam date, study preferences and email preferences</li>
                <li>Every practice session, mock exam, answer, score and wrong-answer record</li>
                <li>Your flashcards, decks, review schedules and study plan</li>
                <li>Your streak, progress history and the adaptive model built from your answers</li>
                <li>Simulation sessions and any feedback you sent on a question</li>
                <li>Notifications and push-notification tokens for your devices</li>
              </ul>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">
                4. What we keep, and for how long
              </h2>
              <p className="mb-2 text-neutral-700">
                A small amount of information survives deletion, because the law or the payment
                network requires it. None of it can be used to sign in as you:
              </p>
              <ul className="list-disc pl-5 mb-4 text-neutral-700">
                <li>
                  <strong>Payment and refund records</strong> (held by our payment processor, Stripe):
                  kept for up to <strong>7 years</strong> to meet tax and accounting obligations. We
                  never store your card details.
                </li>
                <li>
                  <strong>Email delivery records</strong>: that a message was sent to your address
                  and whether it was delivered: kept for up to <strong>24 months</strong>, then
                  deleted.
                </li>
                <li>
                  <strong>Product usage events</strong> stored under an internal account identifier
                  (not your name or email): kept for up to <strong>24 months</strong>, then deleted.
                </li>
                <li>
                  <strong>Anything you asked us to publish</strong>, such as a testimonial or a
                  pass story. Tell us in your deletion email and we will remove it too.
                </li>
                <li>
                  <strong>Encrypted backups</strong> may still contain deleted data for up to{' '}
                  <strong>30 days</strong> before they are overwritten.
                </li>
              </ul>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">5. Subscriptions</h2>
              <p className="mb-4 text-neutral-700">
                If you pay us directly, we cancel your subscription first and stop the deletion if
                that cancellation fails, so billing can never continue for an account that no longer
                exists. If you subscribed through <strong>Google Play</strong> or the{' '}
                <strong>App Store</strong>, cancel it there as well. Those subscriptions are held by
                the store and we cannot cancel them for you. Deleting your account does not
                automatically issue a refund; email us if you believe one is owed.
              </p>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">
                6. If you only want the emails to stop
              </h2>
              <p className="mb-4 text-neutral-700">
                You do not have to delete your account to stop hearing from us. Use the unsubscribe
                link at the bottom of any email, or email {SUPPORT_EMAIL} and ask. Your progress
                stays where it is.
              </p>

              <h2 className="text-xl font-semibold mb-3 text-neutral-900">7. Questions</h2>
              <p className="mb-4 text-neutral-700">
                Email <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                Our <Link className="underline" to="/privacy-policy">Privacy Policy</Link> covers how
                we handle your information more generally.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAccount;
